import React from "react";
import {connect} from "react-redux";
import {Query} from "react-apollo";
import {fromJS} from "immutable";
import PanelGroupController from "../../../components/PanelGroupController";
import PropertiesList from "../../../components/panels/PropertiesList";
import ActionButton from "../../../components/ActionButton";
import Loading from "../../../components/Loading";
import GraphQLErrors from "../../../components/GraphQLErrors";
import StatusStepsPanel from "../../shared/StatusStepsPanel";
import {GET_PURCHASE_ORDER} from "../../../queries/purchaseOrders";
import {statusLabel} from "../statusDisplay";
import {
  requestEditPurchaseOrder,
  requestEditPurchaseOrderLines,
  requestPlacePurchaseOrder,
  requestReceivePurchaseOrder,
  requestCancelPurchaseOrder,
} from "../../../actions/PurchaseOrderApiActions";
import gql from "graphql-tag";
import {AdviceSentButton, adviceSentText} from "../../shared/AdviceSent";

// The side panels of a purchase order: its status, its details, and what can
// be done with it. Its lines stay in the main area: they are the order.

const STEPS = [
  {id: "draft", title: "Draft"},
  {id: "ordered", title: "Ordered"},
  {id: "received", title: "Received"},
];

const withOrder = (purchaseOrderId, render) => (
  <Query query={GET_PURCHASE_ORDER} variables={{id: purchaseOrderId}} fetchPolicy="cache-and-network">
    {(result) => render(result, result.data && result.data.purchaseOrder)}
  </Query>
);

export const PurchaseOrderStatusPanel = ({purchaseOrderId}) =>
  withOrder(purchaseOrderId, (_result, order) => (
    <StatusStepsPanel
      title="Order Status"
      steps={STEPS}
      status={order && order.status}
      cancelledText={order && order.cancelReason ? `This purchase order was cancelled: ${order.cancelReason}` : "This purchase order was cancelled."} />
  ));

const PROPERTIES = fromJS([
  {prop: "id", label: "Id", type: "id"},
  {prop: "statusLabel", label: "Status", type: "string"},
  {prop: "poNumber", label: "PO Number", type: "string"},
  {prop: "partnerName", label: "Supplier", type: "string"},
  {prop: "supplierReference", label: "Supplier Reference", type: "string"},
  {prop: "destinationWarehouseId", label: "Deliver To", type: "id"},
  {prop: "orderDate", label: "Order Date", type: "string"},
  {prop: "expectedDeliveryDate", label: "Expected Delivery", type: "string"},
  {prop: "transportType", label: "Transport", type: "string"},
  {prop: "carrierReference", label: "Truck / Container", type: "string"},
  {prop: "adviceSentText", label: "Shipment Advice", type: "string"},
  {prop: "orderedAt", label: "Placed", type: "utc"},
  {prop: "receivedAt", label: "Received", type: "utc"},
  {prop: "notes", label: "Notes", type: "string"},
  {prop: "totalQuantity", label: "Total Quantity", type: "number"},
]);

export const PurchaseOrderInfoPanel = ({purchaseOrderId, expanded}) => (
  <PanelGroupController title="Order Info" expanded={expanded !== false}>
    {withOrder(purchaseOrderId, ({loading, error}, data) => {
      if (loading && !data) return <Loading />
      if (error) return <GraphQLErrors error={error} />
      if (!data) return <p className="u-no-selection">Purchase order not found</p>
      const order = fromJS(data);
      return <PropertiesList properties={PROPERTIES} data={order
        .set("statusLabel", statusLabel(order.get("status")))
        .set("adviceSentText", adviceSentText(data))} />
    })}
  </PanelGroupController>
);

const SET_ADVICE_SENT = gql`
  mutation setPurchaseOrderAdviceSent($data: SetPurchaseOrderAdviceSentInput!) {
    setPurchaseOrderAdviceSent(input: $data) {
      errors { key message }
    }
  }
`;

const ActionsPanel = (props) => {
  const id = props.purchaseOrderId;
  return withOrder(id, (_result, order) => {
    const status = order && order.status;
    const isDraft = status === "draft";
    const isOrdered = status === "ordered";
    return (
      <PanelGroupController title="Actions">
        <ActionButton label="Edit" active={isDraft || isOrdered} onClick={() => props.requestEditPurchaseOrder(id)} />
        <ActionButton label="Edit Lines" active={isDraft} onClick={() => props.requestEditPurchaseOrderLines(id)} />
        <ActionButton label="Place Order" active={isDraft} onClick={() => props.requestPlacePurchaseOrder(id)} />
        <ActionButton label="Receive" active={isOrdered} onClick={() => props.requestReceivePurchaseOrder(id)} />
        <AdviceSentButton
          mutation={SET_ADVICE_SENT}
          resultField="setPurchaseOrderAdviceSent"
          input={{purchaseOrderId: id}}
          sent={!!(order && order.adviceSent)}
          active={!!status && status !== "cancelled"} />
        <ActionButton label="Cancel Order" active={isDraft || isOrdered} onClick={() => props.requestCancelPurchaseOrder(id)} />
      </PanelGroupController>
    )
  });
}

export const PurchaseOrderActionsPanel = connect(null, {
  requestEditPurchaseOrder,
  requestEditPurchaseOrderLines,
  requestPlacePurchaseOrder,
  requestReceivePurchaseOrder,
  requestCancelPurchaseOrder,
})(ActionsPanel);
